/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import {
  LocalizedPolicy,
  LocalizedPolicyInformation,
  PolicyList,
  Policy,
  PolicyVersion,
} from "../../../api/legal/models/policies";
import { isRight } from "fp-ts/lib/Either";

interface GetPolicyInfoResponse {
  policyName?: string;
  namespace?: string;
  countryCode?: string;
  localeId?: string;
  docDate?: string; 
}
interface GetPolicyInfoParam {
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation;
  policies: PolicyList | null;
}

export const getLocalizedPolicyInfo = ({ localizedPolicy, policies }: GetPolicyInfoParam): GetPolicyInfoResponse => {
  let result: GetPolicyInfoResponse = {};
  if (!localizedPolicy) return result;

  if (isRight(LocalizedPolicyInformation.decode(localizedPolicy))) {
    const policy = (localizedPolicy as LocalizedPolicyInformation).policy;
    if (policy) {
      result = Object.assign({}, result, {
        policyName: policy.policyName,
        namespace: policy.namespace,
        countryCode: policy.countryCode,
        localeId: localizedPolicy.localeCode,
        docDate: localizedPolicy.updatedAt ? localizedPolicy.updatedAt : localizedPolicy.createdAt,
      });
    }
  }
  if ((!result.namespace || !result.countryCode || !result.policyName) && Array.isArray(policies)) {
    policies.forEach((policy: Policy) => {
      if (!Array.isArray(policy.policyVersions)) {
        return;
      }
      policy.policyVersions.forEach((version: PolicyVersion) => {
        if (!Array.isArray(version.localizedPolicyVersions)) {
          return;
        }
        version.localizedPolicyVersions.forEach((localized: LocalizedPolicy) => {
          if (localized.id === localizedPolicy.id) {
            result = Object.assign({}, result, {
              policyName: policy.policyName,
              namespace: policy.namespace,
              countryCode: policy.countryCode,
              localeId: localizedPolicy.localeCode,
              docDate: localizedPolicy.updatedAt ? localizedPolicy.updatedAt : localizedPolicy.createdAt,
            });
          }
        });
      });
    });
  }
  return result;
};

export const getLanguagesListFromPoliciesList = (policies: PolicyList): string[] => {
  const languages: { [key: string]: boolean } = {};

  policies.forEach((policy: Policy) => {
    if (!!policy.policyVersions) {
      policy.policyVersions.forEach((versions: PolicyVersion) => {
        if (versions.localizedPolicyVersions) {
          versions.localizedPolicyVersions.forEach((version: LocalizedPolicy) => {
            languages[version.localeCode] = true;
          });
        }
      });
    }
  });

  return Object.keys(languages);
};

export const getLocalizedPolicyVersionPublishDateFromPolicies = ({
  localizedPolicy,
  policies,
}: {
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation;
  policies: PolicyList | null;
}): string | null => {
  let publishDateResult: string | null = null;

  if (
    isRight(LocalizedPolicyInformation.decode(localizedPolicy)) &&
    !!(localizedPolicy as LocalizedPolicyInformation).policyVersion
  ) {
    const { publishedDate } = (localizedPolicy as LocalizedPolicyInformation).policyVersion as PolicyVersion;
    publishDateResult = publishedDate || null;
  } else if (isRight(LocalizedPolicy.decode(localizedPolicy)) && Array.isArray(policies)) {
    policies.forEach((policy: Policy) => {
      if (!!publishDateResult || !Array.isArray(policy.policyVersions)) return;
      policy.policyVersions.forEach((version: PolicyVersion) => {
        if (!!publishDateResult || !version.publishedDate || !Array.isArray(version.localizedPolicyVersions)) return;
        version.localizedPolicyVersions.forEach((localizedVersion: LocalizedPolicy) => {
          if (localizedVersion.id !== localizedPolicy.id) return;
          publishDateResult = version.publishedDate || null;
        });
      });
    });
  }
  return publishDateResult;
};
